import { useState } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import Sidebar, { SidebarNav, SidebarBrand } from "./Sidebar";
import Topbar from "./Topbar";
import DailyAssignmentModal from "./DailyAssignmentModal";
import MaintenanceBanner from "./MaintenanceBanner";
import { cn } from "@/lib/utils";
import { ChevronRight, Home, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LABELS = {
  strutture: "Strutture", controlli: "Controlli", ispezione: "Ispezione", relazioni: "Relazioni",
  alert: "Alert", performance: "Performance", housekeeping: "Housekeeping", staff: "Staff",
  report: "Report", templates: "Templates", configurazione: "Configurazione", valutazioni: "Questionario",
  utenti: "Utenti", profilo: "Profilo", notifiche: "Notifiche", "audit-log": "Audit log",
};

function Breadcrumb() {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "/login") return null;
  const parts = pathname.split("/").filter(Boolean);
  return (
    <nav className="px-4 sm:px-6 md:px-8 pt-4 text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid="breadcrumb">
      <Link to="/" className="flex items-center gap-1 hover:text-foreground"><Home className="h-3 w-3" /> Dashboard</Link>
      {parts.map((p, i) => {
        const href = "/" + parts.slice(0, i + 1).join("/");
        const isLast = i === parts.length - 1;
        const label = LABELS[p] || (p.length > 12 ? p.slice(0, 8) + "…" : p);
        return (
          <span key={href} className="flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3 opacity-60" />
            {isLast ? <span className="text-foreground font-medium">{label}</span> : <Link to={href} className="hover:text-foreground">{label}</Link>}
          </span>
        );
      })}
    </nav>
  );
}

function MobileMenuButton({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden h-10 w-10 rounded-xl border border-border/60 hover:bg-accent/40 flex items-center justify-center mr-2"
          aria-label="Apri menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 ih-glass-strong" data-testid="mobile-sidebar">
        <div className="flex flex-col h-full">
          <SidebarBrand collapsed={false} />
          <SidebarNav collapsed={false} onItemClick={() => onOpenChange(false)} />
          <div className="px-4 py-3 border-t border-border/50 text-center text-[10px] tracking-wide text-muted-foreground/60" data-testid="ptk-credit-mobile">
            Dev by PTK Solutions ® 2026
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen ih-bg-ambient">
      <MaintenanceBanner />
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className={cn("transition-[padding] duration-300 pl-0", collapsed ? "md:pl-[80px]" : "md:pl-[260px]")}>
        <div className="relative">
          <div className="md:hidden absolute top-3 left-3 z-40">
            <MobileMenuButton open={mobileOpen} onOpenChange={setMobileOpen} />
          </div>
          <Topbar />
        </div>
        <Breadcrumb />
        <main className="p-4 sm:p-6 md:p-8 max-w-[1500px] mx-auto flex flex-col min-h-[calc(100vh-4rem)]" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <DailyAssignmentModal />
    </div>
  );
}
